import moment from 'moment'

import type { GateFeedFilter } from '../../clients/type'

import { ROW_TRUCK_FUEL_TYPE_OPTIONS, TRUCK_CLASS_OPTIONS } from './constants'
import { AssetData } from './types'
import {
  ChassisTypeDirectory,
  LoadTypeDirectory,
  VehicleTypeDirectory,
} from '../../models/client/metadata'

export function getAllowedSites(user, locations) {
  let allowedSites = []

  if (locations) {
    Object.keys(locations).forEach((location) => {
      const locationData = locations[location]
      allowedSites.push({
        ...locationData,
        label: locationData?.name,
        value: locationData?.id,
      })
    })
    if (user && user.meta.sites) {
      allowedSites = allowedSites.filter((site) => user.meta.sites.includes(site.alias))
    }
  }
  return allowedSites
}

export function getSelectOptions(values = {}) {
  return Object.keys(values).map((key) => ({
    label: values[key].name,
    value: values[key].id,
  }))
}

export function getBigNumbersTitle(title: string) {
  const titleMap: Record<string, string> = {
    all_assets: 'All assets',
    tractor: 'Tractor',
    forklift: 'Forklift',
    service: 'Service',
    container: 'Container',
    reefer: 'Container Reefer',
    chassis: 'Chassis',
    isBare: 'Bare Chassis',
    dryvan: 'Dry Van',
    dryvan_reefer: 'Dry Van Reefer',
    flatbed: 'Flatbed',
    other: 'Other',
    goat: 'GOAT',
  }

  return titleMap[title]
}

export function getBigNumbersCount(stats, optionStats) {
  if (optionStats?.name === 'all_assets') {
    const statsWithoutBareChassis = stats.filter((stat) => stat.name !== 'Bare Chassis')
    return statsWithoutBareChassis.reduce((acc, curr) => Number(acc) + Number(curr.count), 0)
  }
  return optionStats?.count ?? '-'
}

export function getRowValue(rowType: string, row): string {
  switch (rowType) {
    case 'assetType':
      if (row?.attachmentName) {
        return row.attachmentName
      }
      if (row?.assetName) {
        return row.assetName
      }
      if (row?.vehicleTypeName) {
        return row?.vehicleTypeName
      }
      return 'Unkown'
    case 'DATE':
      return formatTime(row?.detectedAt)
    case 'loadNumber':
      return row?.loadNumber || '-'
    case 'hasSeal':
      return row?.hasSeal || '-'
    case 'sealNumber':
      return row?.sealNumber || '-'
    case 'loadLicensePlate':
      return row?.loadLicensePlate || '-'
    case 'loadDamage':
      return row?.loadDamage || '-'
    case 'loadRemarks':
      return row?.loadRemarks || '-'
    case 'chassisNumber':
      return row?.chassisNumber || '-'
    case 'chassisLicensePlate':
      return row?.chassisLicensePlate || '-'
    case 'chassisDamage':
      return row?.chassisDamage || '-'
    case 'chassisRemarks':
      return row?.chassisRemarks || '-'
    case 'vehicleCarrierName':
      return row?.carrierName || row?.vehicle?.carrier?.name || '-'
    case 'vehicleNumber':
      return row?.vehicleNumber || '-'
    case 'vehicleRemarks':
      return row?.vehicleRemarks || '-'
    case 'vehicleFuelType':
      return getTruckFuelType(row?.vehicleFuelType)
    case 'vehicleDotNumber':
      return row?.vehicleDotNumber || '-'
    case 'vehicleLicensePlate':
      return row?.vehicleLicensePlate || '-'
    case 'vehicleDamage':
      return row?.vehicleDamage || '-'
    case 'vehicleWeightClass':
      return getTruckClass(row?.vehicleWeightClass)
    case 'vehicleDwellTimeHr':
      if (row?.vehicleId) {
        return getAssetDwellTime(row?.vehicleDwellTime)
      }
      if (row?.chassisId) {
        return getAssetDwellTime(row?.chassisDwellTime)
      }
      if (row?.loadId) {
        return getAssetDwellTime(row?.loadDwellTime)
      }
    default:
      return null
  }
}

export function formatTime(time: string, format = 'MM/DD/YYYY - hh:mm A') {
  return moment(time).format(format)
}

export function getTruckClass(truckClass: string) {
  return TRUCK_CLASS_OPTIONS.find((option) => option.value === truckClass)?.label || '-'
}

export function getTruckFuelType(fuelType: string) {
  return ROW_TRUCK_FUEL_TYPE_OPTIONS.find((option) => option.value === fuelType)?.label || '-'
}

export const getAssetDwellTime = (dwellTime?: number) => {
  if (dwellTime) {
    dwellTime = Number(dwellTime)
    const d = Math.floor(dwellTime / (3600 * 24))
    const h = Math.floor((dwellTime % (3600 * 24)) / 3600)
    const m = Math.floor((dwellTime % 3600) / 60)

    const dDisplay = d > 0 ? d + (d == 1 ? ' day, ' : ' days, ') : ''
    const hDisplay = h > 0 ? h + (h == 1 ? ' hr, ' : ' hrs, ') : ''
    const mDisplay = d === 0 && h === 0 && m === 0 ? '0 mins' : m + (m == 1 ? ' min ' : ' mins ')

    return dDisplay + hDisplay + mDisplay
  }
  return '-'
}

export function applyBigNumbersFilter(
  bigNumberOption: string,
  siteId: string,
  filterId: string,
  addFilter: (filter: GateFeedFilter) => void
) {
  // apply site filter
  addFilter({
    type: 'site',
    id: siteId,
    label: 'Site ID',
    value: {
      label: 'Current site',
      value: siteId || '',
    },
  })

  // apply big number filter
  switch (bigNumberOption) {
    case 'all_assets':
      break
    case 'tractor':
      addFilter({
        type: 'vehicleType',
        id: 'tractor',
        label: 'Tractor',
        value: filterId,
      })
      break
    case 'forklift':
      addFilter({
        type: 'vehicleType',
        id: 'forklift',
        label: 'Forklift',
        value: filterId,
      })
      break
    case 'service':
      addFilter({
        type: 'vehicleType',
        id: 'service',
        label: 'Service',
        value: filterId,
      })
      break
    case 'container':
      addFilter({
        type: 'loadType',
        id: 'container',
        label: 'Container',
        value: filterId,
      })
      break
    case 'reefer':
      addFilter({
        type: 'loadType',
        id: 'reefer',
        label: 'Container Reefer',
        value: filterId,
      })
      break
    case 'chassis':
      addFilter({
        type: 'chassisType',
        id: 'chassis',
        label: 'Chassis',
        value: filterId,
      })
      break
    case 'isBare':
      addFilter({
        type: 'isBare',
        id: 'isBare',
        label: 'Bare Chassis',
        value: true,
      })
      break
    case 'dryvan':
      addFilter({
        type: 'loadType',
        id: 'dryvan',
        label: 'Dry Van',
        value: filterId,
      })
      break
    case 'dryvan_reefer':
      addFilter({
        type: 'loadType',
        id: 'dryvan_reefer',
        label: 'Dry Van Reefer',
        value: filterId,
      })
      break
    case 'flatbed':
      addFilter({
        type: 'loadType',
        id: 'flatbed',
        label: 'Flatbed',
        value: filterId,
      })
      break
    case 'goat':
      addFilter({
        type: 'vehicleType',
        id: 'goat',
        label: 'GOAT',
        value: filterId,
      })
      break
    case 'other':
      addFilter({
        type: 'vehicleType',
        id: 'other',
        label: 'Other',
        value: filterId,
      })
      break
    default:
      break
  }
}

export function getFormInitialValues(
  assetData?: AssetData,
  sites?: any,
  gates?: any,
  defaultVehicleType?: any
) {
  const currentDate = moment()
  const currentTime = currentDate.format('HH:mm')

  const initialLocation = assetData?.gate?.siteId
    ? {
        label: assetData?.gate?.site?.name,
        value: assetData?.gate?.siteId,
      }
    : {
        label: sites?.[0]?.label,
        value: sites?.[0]?.value,
      }

  const initialGate = assetData?.gate?.id
    ? {
        label: assetData?.gate?.name,
        value: assetData?.gate?.id,
      }
    : {
        label: gates?.[0]?.label,
        value: gates?.[0]?.value,
      }

  const initialValues = {
    location: initialLocation,
    gate: initialGate,
    loadNumber: assetData?.load?.number || null,
    assetStatus: 'in-the-yard',
    ingateDate: assetData?.detectedAt || currentDate.toISOString(),
    outgateDate: assetData?.updatedAt || currentDate.toISOString(),
    ingateTime: assetData?.detectedAt ? getFormTime(assetData.detectedAt) : currentTime,
    outgateTime: assetData?.updatedAt ? getFormTime(assetData.updatedAt) : currentTime,
    loadType: {
      label: assetData?.load?.type?.name,
      value: assetData?.load?.type?.id,
    },
    sealPresent: assetData?.hasSeal || false,
    sealNumber: assetData?.sealNumber || null,
    loadLPNumber: assetData?.load?.licensePlate || null,
    bco: assetData?.load?.bco || null,
    loadRemarks: assetData?.loadRemarks || null,
    chassisNumber: assetData?.chassis?.number || null,
    chassisLPNumber: assetData?.chassis?.licensePlate || null,
    chassisRemarks: assetData?.chassisRemarks || null,
    vehicleType: {
      label: assetData?.vehicle?.type?.name || defaultVehicleType?.label,
      value: assetData?.vehicle?.type?.id || defaultVehicleType?.value,
    },
    carrier: {
      label: assetData?.vehicle?.carrier?.name,
      value: assetData?.vehicle?.carrier?.id,
    },
    truckNumber: assetData?.vehicle?.number || null,
    truckDOT: assetData?.vehicle?.dotNumber || null,
    truckLPNumber: assetData?.vehicle?.licensePlate || null,
    truckClass: {
      label: assetData?.vehicle?.weightClass,
      value: assetData?.vehicle?.weightClass,
    },
    fuelType: {
      label: assetData?.vehicle?.fuelType,
      value: assetData?.vehicle?.fuelType,
    },
    vehicleRemarks: assetData?.vehicleRemarks || null,
  }

  return initialValues
}

export function getFormTime(time: string) {
  return moment(time).format('HH:mm')
}

export function hasAttachmentData(attachmentData) {
  return attachmentData ? attachmentData.some((attachment) => attachment.isAttachment) : null
}

export function getAttachmentDetails(attachment) {
  if (attachment.assetName === 'Chassis') {
    return `Chassis - ${attachment.chassisNumber || 'Number not provided'}`
  }
  return `${attachment.loadType} - ${attachment.loadNumber || 'Number not provided'}`
}

export function getOutgateEvent(outwardObservation, assetType) {
  return outwardObservation ? `${assetType} - ${formatTime(outwardObservation.detectedAt)}` : null
}

export function getBigNumberValues(
  vehicleTypes?: VehicleTypeDirectory,
  loadTypes?: LoadTypeDirectory,
  chassisType?: ChassisTypeDirectory
) {
  const bigNumbers = [
    {
      label: 'All assets',
      name: 'all_assets',
      id: 'all_assets',
    },
    {
      label: 'Vehicle Tractor',
      name: 'tractor',
      id: vehicleTypes?.Tractor?.id,
    },
    {
      label: 'Vehicle Forklift',
      name: 'forklift',
      id: vehicleTypes?.Forklift?.id,
    },
    {
      label: 'Vehicle Service',
      name: 'service',
      id: vehicleTypes?.Service?.id,
    },
    {
      label: 'Load Container',
      name: 'container',
      id: loadTypes?.Container?.id,
    },
    {
      label: 'Load Container Reefer',
      name: 'reefer',
      id: loadTypes?.['Container Reefer']?.id,
    },
    {
      label: 'Chassis',
      name: 'chassis',
      id: chassisType?.Default?.id,
    },
    {
      label: 'Load Dry Van',
      name: 'dryvan',
      id: loadTypes?.['Dry Van']?.id,
    },
    {
      label: 'Load Dry Van Reefer',
      name: 'dryvan_reefer',
      id: loadTypes?.['Dry Van Reefer']?.id,
    },
    {
      label: 'Load Flatbed',
      name: 'flatbed',
      id: loadTypes?.Flatbed?.id,
    },
    {
      label: 'Vehicle GOAT',
      name: 'goat',
      id: vehicleTypes?.GOAT?.id,
    },
    {
      label: 'Vehicle Other',
      name: 'other',
      id: vehicleTypes?.Other?.id,
    },
    {
      label: 'Bare Chassis',
      name: 'isBare',
      id: '',
    },
  ]

  return bigNumbers
}
